import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { billingRateAPI, customerAPI, projectAPI } from '../services/api';
import type { BillingRate, Customer, Project } from '../types';

const BillingRateList: React.FC = () => {
  const [rates, setRates] = useState<BillingRate[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    customer: '',
    project: '',
    is_active: '',
  });

  useEffect(() => {
    fetchCustomers();
    fetchProjects();
    fetchRates();
  }, []);

  useEffect(() => {
    fetchRates();
  }, [filters]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.results);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchRates = async () => {
    try {
      const params: any = {};
      if (filters.customer) params.customer = filters.customer;
      if (filters.project) params.project = filters.project;
      if (filters.is_active) params.is_active = filters.is_active;
      
      const response = await billingRateAPI.getAll(params);
      setRates(response.data.results);
    } catch (error) {
      console.error('Failed to fetch billing rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ customer: '', project: '', is_active: '' });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this billing rate?')) {
      try {
        await billingRateAPI.delete(id);
        fetchRates();
      } catch (error) {
        console.error('Failed to delete billing rate:', error);
        alert('Failed to delete billing rate');
      }
    }
  };

  if (loading) return <div className="container">Loading...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Billing Rates</h2>
          <Link to="/billing-rates/new" className="btn btn-primary">
            Create New Rate
          </Link>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Company
            </label>
            <select
              value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Companies</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Project
            </label>
            <select
              value={filters.project}
              onChange={(e) => setFilters({ ...filters, project: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.code} - {project.name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Status
            </label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {rates.length === 0 ? (
          <p>No billing rates found. {filters.customer || filters.project || filters.is_active ? 'Try adjusting your filters.' : 'Create your first rate to get started.'}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Rate Name</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Users</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr key={rate.id}>
                    <td>
                      <span style={{ 
                        color: '#3498db', 
                        fontWeight: 'bold',
                        backgroundColor: '#ecf6fc',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                      }}>
                        {rate.project_code}
                      </span>
                    </td>
                    <td><strong>{rate.name}</strong></td>
                    <td>${rate.rate_amount}/hr</td>
                    <td style={{ maxWidth: '250px' }}>{rate.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${rate.is_active ? 'active' : 'inactive'}`}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {!rate.is_billable && (
                        <span style={{
                          display: 'inline-block',
                          marginLeft: '0.5rem',
                          padding: '0.15rem 0.4rem',
                          backgroundColor: '#f8d7da',
                          color: '#721c24',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}>
                          Non-billable
                        </span>
                      )}
                    </td>
                    <td>{rate.users_count || 0}</td>
                    <td className="actions">
                      <Link to={`/billing-rates/${rate.id}/edit`} className="btn btn-secondary btn-small">
                        Edit
                      </Link>
                      <button
                        onClick={() => !rate.has_time_entries && handleDelete(rate.id!)}
                        className="btn btn-danger btn-small"
                        disabled={rate.has_time_entries}
                        style={{
                          opacity: rate.has_time_entries ? 0.5 : 1,
                          cursor: rate.has_time_entries ? 'not-allowed' : 'pointer',
                        }}
                        title={
                          rate.has_time_entries
                            ? `Cannot delete: ${rate.time_entries_count} time ${rate.time_entries_count === 1 ? 'entry' : 'entries'} exist for this rate`
                            : 'Delete this billing rate'
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingRateList;

